<template>
  <div class="w-full">
    <div class="mb-4">
      <vs-row vs-type="flex" vs-justify="space-between" vs-align="center" vs-w="12">
        <vs-col vs-w="8">
          <h3 style="color: #b4aa99">Configuração de Notas Fiscais (NFS-e)</h3>
          <p class="text-sm text-gray-500 mt-1">
            Gerenciamento de templates de observação e alíquotas efetivas mensais por empresa.
          </p>
        </vs-col>
        <vs-col vs-w="4" vs-type="flex" vs-justify="flex-end" vs-align="center">
          <div class="w-full max-w-xs">
            <label class="text-xs font-semibold text-gray-600 block mb-1">Empresa Selecionada</label>
            <el-select
              v-model="empresaSelecionadaId"
              class="w-full"
              placeholder="Selecione a empresa"
              @change="aoTrocarEmpresa"
              filterable
            >
              <el-option
                v-for="item in empresas"
                :key="item.id"
                :label="item.nome_fantasia + ' (' + (item.cnpj || 'Sem CNPJ') + ')'"
                :value="item.id"
              />
            </el-select>
          </div>
        </vs-col>
      </vs-row>
    </div>

    <div v-if="!empresaSelecionadaId" class="mt-8 text-center text-gray-500">
      <p>Selecione uma empresa acima para visualizar e editar as configurações.</p>
    </div>

    <div v-else class="mt-4">
      <vs-tabs class="tabs-shadow-none">
        <!-- ABA 1: TEMPLATES DA NFS-E -->
        <vs-tab label="Templates da NFS-e" icon-pack="feather" icon="icon-file-text">
          <div class="mt-4">
            <vx-card no-shadow card-border title="Templates de Observação da NFS-e">
              <vs-row vs-w="12" class="mb-4">
                <vs-col vs-w="12">
                  <div class="bg-gray-100 p-3 rounded mb-4">
                    <p class="text-xs font-bold text-gray-700 uppercase mb-2">Placeholders Disponíveis:</p>
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-for="ph in placeholdersDisponiveis"
                        :key="ph.chave"
                        class="cursor-pointer bg-white border border-gray-300 text-gray-800 text-xs px-2 py-1 rounded hover:bg-gray-200"
                        :title="ph.descricao"
                        @mousedown.prevent="inserirPlaceholder(ph.chave)"
                      >
                        {{ ph.chave }}
                      </span>
                    </div>
                    <span class="text-xs text-gray-500 block mt-2">
                      Clique em qualquer placeholder para inseri-lo na posição do cursor do campo focado.
                    </span>
                  </div>
                </vs-col>

                <!-- Campo 1: Template da Descrição do Serviço (xDescServ) -->
                <vs-col vs-w="12" class="mb-4">
                  <label class="vs-input--label font-bold">
                    Template da Descrição dos Serviços (xDescServ)
                  </label>
                  <vs-textarea
                    ref="textareaDescricao"
                    v-model="templates.template_descricao_servico"
                    class="w-full mt-1"
                    height="120"
                    placeholder="Ex: {contrato_controle} com {contrato_garantia} meses de garantia. CONTRATO {contrato_id}. FATURA {fatura_parcela}. {forma_pagamento}. PARCELAS: {parcelas} TRIBUTOS APROXIMADOS de {tributos_aproximados}. {texto_dispensa_retencao}"
                    @focus="campoFocado = 'descricao'"
                  />
                  <span class="text-gray-500 text-xs block mt-1">
                    Texto principal enviado na discriminação do serviço da NFS-e. Obrigatório para emissão.
                  </span>
                </vs-col>

                <!-- Campo 2: Template de Informações Complementares (xInfComp) -->
                <vs-col vs-w="12" class="mb-4">
                  <label class="vs-input--label font-bold">
                    Template de Informações Complementares (xInfComp)
                  </label>
                  <vs-textarea
                    ref="textareaInfoCompl"
                    v-model="templates.template_informacoes_complementares"
                    class="w-full mt-1"
                    height="80"
                    placeholder="Ex: {texto_beneficio_fiscal}"
                    @focus="campoFocado = 'infoCompl'"
                  />
                  <span class="text-gray-500 text-xs block mt-1">
                    Informações adicionais/complementares da NFS-e (ex: transparência tributária, mandado de segurança).
                  </span>
                </vs-col>
              </vs-row>

              <vs-row vs-w="12" vs-type="flex" vs-justify="flex-end" vs-align="center">
                <vs-button
                  color="success"
                  type="relief"
                  icon-pack="feather"
                  icon="icon-save"
                  :disabled="salvandoTemplates || !temPermissao"
                  @click="salvarTemplates"
                >
                  Salvar Templates
                </vs-button>
              </vs-row>
            </vx-card>
          </div>
        </vs-tab>

        <!-- ABA 2: ALÍQUOTA EFETIVA POR COMPETÊNCIA (CRUD) -->
        <vs-tab label="Alíquota Efetiva Mensal" icon-pack="feather" icon="icon-percent">
          <div class="mt-4">
            <vx-card no-shadow card-border>
              <div class="flex justify-between items-center mb-4">
                <div>
                  <h4 class="font-bold text-gray-800">Histórico de Alíquotas Efetivas</h4>
                  <p class="text-xs text-gray-500 mt-1">
                    Alíquota mensal utilizada para o cálculo de tributos aproximados nas notas fiscais emitidas na competência.
                  </p>
                </div>
                <vs-button
                  color="primary"
                  type="relief"
                  icon-pack="feather"
                  icon="icon-plus"
                  :disabled="!temPermissao"
                  @click="abrirModalNovaAliquota"
                >
                  Cadastrar Alíquota
                </vs-button>
              </div>

              <!-- Alerta se não houver alíquota para a competência atual -->
              <vs-alert
                v-if="!aliquotaVigenteNaCompetenciaAtual"
                color="warning"
                icon="warning"
                active="true"
                class="mb-4"
              >
                <b>Atenção:</b> Esta empresa não possui alíquota efetiva cadastrada para a competência vigente
                <span v-if="competenciaReferencia">({{ formatarCompetenciaDisplay(competenciaReferencia) }} - {{ competenciaReferencia }})</span>
                <span v-else>(competência não informada pelo servidor)</span>.
                Cadastre a alíquota mensal para permitir a emissão de notas fiscais sem bloqueio.
              </vs-alert>

              <vs-table
                :data="aliquotas"
                noDataText="Nenhuma alíquota cadastrada para esta empresa"
                pagination
                max-items="12"
              >
                <template slot="thead">
                  <vs-th sort-key="competencia">Competência</vs-th>
                  <vs-th sort-key="percentual">Alíquota Efetiva (%)</vs-th>
                  <vs-th>Status</vs-th>
                  <vs-th>Atualizado em</vs-th>
                  <vs-th class="text-center">Ações</vs-th>
                </template>

                <template slot-scope="{ data }">
                  <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                    <vs-td :data="tr.competencia">
                      <b>{{ formatarCompetenciaDisplay(tr.competencia) }}</b>
                      <span class="text-xs text-gray-500 block">({{ tr.competencia ? String(tr.competencia).slice(0, 10) : '' }})</span>
                    </vs-td>

                    <vs-td :data="tr.percentual">
                      <span class="text-base font-semibold">{{ parseFloat(tr.percentual).toFixed(2) }}%</span>
                    </vs-td>

                    <vs-td>
                      <vs-chip v-if="ehCompetenciaAtual(tr.competencia)" color="success">
                        Vigente
                      </vs-chip>
                      <vs-chip v-else color="dark">
                        Histórico
                      </vs-chip>
                    </vs-td>

                    <vs-td :data="tr.updated_at">
                      {{ tr.updated_at ? String(tr.updated_at).slice(0, 10).split('-').reverse().join('/') : '-' }}
                    </vs-td>

                    <vs-td class="text-center">
                      <div class="flex justify-center items-center gap-2">
                        <vs-button
                          size="small"
                          color="warning"
                          type="border"
                          icon-pack="feather"
                          icon="icon-edit"
                          radius
                          title="Editar alíquota"
                          :disabled="!temPermissao"
                          @click="abrirModalEditarAliquota(tr)"
                        />
                        <vs-button
                          size="small"
                          color="danger"
                          type="border"
                          icon-pack="feather"
                          icon="icon-trash-2"
                          radius
                          title="Excluir alíquota"
                          :disabled="!temPermissao"
                          @click="confirmarExclusaoAliquota(tr)"
                        />
                      </div>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </vx-card>
          </div>
        </vs-tab>
      </vs-tabs>
    </div>

    <!-- MODAL DE CADASTRO / EDIÇÃO DE ALÍQUOTA -->
    <vs-prompt
      :title="isEditingAliquota ? 'Editar Alíquota Efetiva' : 'Nova Alíquota Efetiva'"
      :active.sync="modalAliquota"
      accept-text="Salvar"
      cancel-text="Cancelar"
      @accept="salvarAliquota"
      @close="fecharModalAliquota"
      :is-valid="validarFormAliquota && temPermissao"
    >
      <div class="p-2">
        <div class="mb-4">
          <label class="vs-input--label font-bold">Competência (Mês/Ano)</label>
          <div class="flex items-center gap-2 mt-1">
            <vs-input
              :value="aliquotaForm.competencia ? formatarCompetenciaDisplay(aliquotaForm.competencia) : ''"
              class="w-full"
              readonly
              placeholder="Clique para selecionar o mês/ano"
              icon-pack="feather"
              icon="icon-calendar"
              @click.native="isModalMonthYearActive = true"
            />
            <vs-button
              type="border"
              icon-pack="feather"
              icon="icon-calendar"
              @click="isModalMonthYearActive = true"
            >
              Selecionar
            </vs-button>
          </div>
          <span class="text-xs text-gray-500 block mt-1">
            <template v-if="aliquotaForm.competencia">
              Competência selecionada: <b>{{ formatarCompetenciaDisplay(aliquotaForm.competencia) }}</b> ({{ aliquotaForm.competencia }})
            </template>
            <template v-else>
              Selecione o mês e ano da competência.
            </template>
          </span>
        </div>

        <div class="mb-4">
          <label class="vs-input--label font-bold">Alíquota Efetiva (%)</label>
          <vs-input
            v-model="aliquotaForm.percentual"
            class="w-full mt-1"
            type="number"
            step="0.01"
            min="0"
            max="100"
            icon="%"
            icon-after="true"
            placeholder="Ex: 17.00"
          />
          <span class="text-xs text-gray-500 block mt-1">
            Percentual com até duas casas decimais a ser aplicado sobre o valor bruto.
          </span>
        </div>
      </div>
    </vs-prompt>

    <ModalSelectMonthYear
      :active.sync="isModalMonthYearActive"
      :initial-month="initialMonthAliquota"
      :initial-year="initialYearAliquota"
      @selected="onMonthYearSelectedAliquota"
    />
  </div>
</template>

<script>
import {
  formatarCompetenciaExtenso,
  extrairPlaceholdersInvalidos,
  validarConfiguracaoEmissao
} from "@/utils/templateRenderer.js";
import notaObservacaoTemplateMixin from "@/mixins/notaObservacaoTemplate.js";
import ModalSelectMonthYear from "@/components/default/ModalSelectMonthYear.vue";

export default {
  name: "ConfiguracaoNotasFiscais",
  components: {
    ModalSelectMonthYear
  },
  mixins: [notaObservacaoTemplateMixin],
  data() {
    return {
      empresas: [],
      empresaSelecionadaId: null,
      empresa: null,
      competenciaVigenteEmpresa: "",
      campoFocado: "descricao",
      salvandoTemplates: false,
      requisicoesEmVoo: 0,
      templates: {
        template_descricao_servico: "",
        template_informacoes_complementares: ""
      },
      aliquotas: [],
      aliquotaEfetivaVigente: null,
      modalAliquota: false,
      isEditingAliquota: false,
      isModalMonthYearActive: false,
      aliquotaForm: {
        id: null,
        competencia: "",
        percentual: ""
      },
      placeholdersDisponiveis: [
        { chave: "{contrato_controle}", descricao: "Nome do controle de pragas / serviço do contrato" },
        { chave: "{contrato_garantia}", descricao: "Meses de garantia" },
        { chave: "{contrato_id}", descricao: "ID/Número do contrato" },
        { chave: "{fatura_parcela}", descricao: "Número da parcela da fatura" },
        { chave: "{forma_pagamento}", descricao: "Forma de pagamento em maiúsculas" },
        { chave: "{parcelas}", descricao: "Lista pré-formatada das parcelas (DD/MM/AAAA: R$ XX,XX. )" },
        { chave: "{competencia}", descricao: "Mês/Ano por extenso (ex: SETEMBRO/2026)" },
        { chave: "{vencimento}", descricao: "Data de vencimento da parcela (DD/MM/AAAA)" },
        { chave: "{tributos_aproximados}", descricao: "Valor calculado dos tributos aproximados" },
        { chave: "{texto_dispensa_retencao}", descricao: "Texto legal de dispensa de retenção PIS/COFINS/CSLL/IR" },
        { chave: "{chave_pix}", descricao: "CNPJ da empresa prestadora" },
        { chave: "{texto_beneficio_fiscal}", descricao: "Texto legal ou benefício fiscal cadastrado na empresa" }
      ]
    };
  },
  computed: {
    competenciaReferencia() {
      return this.competenciaVigenteEmpresa || "";
    },
    initialMonthAliquota() {
      const comp = this.aliquotaForm.competencia || this.competenciaReferencia;
      if (comp) {
        const parts = String(comp).split("-");
        if (parts.length >= 2) {
          const m = parseInt(parts[1], 10) - 1;
          if (!isNaN(m) && m >= 0 && m <= 11) return m;
        }
      }
      return new Date().getMonth();
    },
    initialYearAliquota() {
      const comp = this.aliquotaForm.competencia || this.competenciaReferencia;
      if (comp) {
        const parts = String(comp).split("-");
        if (parts.length >= 1) {
          const y = parseInt(parts[0], 10);
          if (!isNaN(y) && y > 1900) return y;
        }
      }
      return new Date().getFullYear();
    },
    aliquotaVigenteNaCompetenciaAtual() {
      if (!this.competenciaReferencia || !this.aliquotas || this.aliquotas.length === 0) return false;
      return this.aliquotas.some(a => a.competencia && String(a.competencia).startsWith(this.competenciaReferencia));
    },
    validarFormAliquota() {
      const comp = this.aliquotaForm.competencia;
      const perc = this.aliquotaForm.percentual;
      if (!comp || !String(comp).trim()) return false;
      if (perc === "" || perc === null || perc === undefined || isNaN(Number(perc))) return false;
      const numPerc = Number(perc);
      if (numPerc < 0 || numPerc > 100) return false;
      return true;
    },
    temPermissao() {
      return !!(
        this.$atividades &&
        this.$atividades.permissoes &&
        Array.isArray(this.$atividades.permissoes) &&
        this.$atividades.permissoes.includes(14)
      );
    }
  },
  methods: {
    async carregarEmpresas() {
      this.$vs.loading();
      try {
        const res = await this.$http.get("empresa");
        this.empresas = Array.isArray(res) ? res : (res && res.data ? res.data : []);

        const queryEmpresaId = this.$route.query.empresa_id
          ? Number(this.$route.query.empresa_id)
          : null;

        const empresaEncontrada = (queryEmpresaId !== null && !isNaN(queryEmpresaId))
          ? this.empresas.find(e => Number(e.id) === queryEmpresaId)
          : null;

        if (empresaEncontrada) {
          this.empresaSelecionadaId = empresaEncontrada.id;
        } else if (this.empresas.length > 0) {
          this.empresaSelecionadaId = this.empresas[0].id;
        }

        if (this.empresaSelecionadaId) {
          await this.carregarDadosEmpresa(this.empresaSelecionadaId);
        }
      } catch (err) {
        await this.tratarErroHttpPadrao(err);
      } finally {
        this.$vs.loading.close();
      }
    },

    async aoTrocarEmpresa(id) {
      if (!id) return;
      await this.carregarDadosEmpresa(id);
    },

    async carregarDadosEmpresa(empresaId) {
      this.requisicoesEmVoo++;
      this.$vs.loading();
      try {
        const [aliquotasRes, templatesAliqRes] = await Promise.all([
          this.$http.get(`empresa_aliquota_efetiva?empresa_id=${empresaId}`),
          this.$http.get(`empresa/${empresaId}/templates-e-aliquota`)
        ]);

        // Race condition: descarta respostas de requisições que chegaram após o usuário já ter selecionado outra empresa
        if (Number(this.empresaSelecionadaId) !== Number(empresaId)) {
          return;
        }

        const empresaRes = this.empresas.find(e => Number(e.id) === Number(empresaId)) || {};
        this.empresa = empresaRes;
        this.templates.template_descricao_servico = (templatesAliqRes && templatesAliqRes.template_descricao_servico !== undefined)
          ? (templatesAliqRes.template_descricao_servico || "")
          : (empresaRes.template_descricao_servico || "");
        this.templates.template_informacoes_complementares = (templatesAliqRes && templatesAliqRes.template_informacoes_complementares !== undefined)
          ? (templatesAliqRes.template_informacoes_complementares || "")
          : (empresaRes.template_informacoes_complementares || "");

        if (templatesAliqRes && templatesAliqRes.competencia) {
          this.competenciaVigenteEmpresa = String(templatesAliqRes.competencia).slice(0, 7);
        } else {
          this.competenciaVigenteEmpresa = "";
        }

        const listaAliquotas = Array.isArray(aliquotasRes)
          ? aliquotasRes
          : (aliquotasRes && aliquotasRes.rows ? aliquotasRes.rows : []);
        this.aliquotas = listaAliquotas;

        let aliqReal = (templatesAliqRes && templatesAliqRes.aliquota_efetiva_vigente != null)
          ? templatesAliqRes.aliquota_efetiva_vigente
          : null;

        if (aliqReal == null && this.competenciaVigenteEmpresa && listaAliquotas.length > 0) {
          const registro = listaAliquotas.find(a => a.competencia && String(a.competencia).startsWith(this.competenciaVigenteEmpresa));
          if (registro && registro.percentual != null && !isNaN(Number(registro.percentual))) {
            aliqReal = parseFloat(registro.percentual);
          }
        }

        this.aliquotaEfetivaVigente = aliqReal;
      } catch (err) {
        await this.tratarErroHttpPadrao(err);
      } finally {
        this.requisicoesEmVoo = Math.max(0, (this.requisicoesEmVoo || 0) - 1);
        if (this.requisicoesEmVoo === 0) {
          this.$vs.loading.close();
        }
      }
    },

    getTextareaElement(campo) {
      const ref = campo === "infoCompl" ? this.$refs.textareaInfoCompl : this.$refs.textareaDescricao;
      if (!ref) return null;
      if (ref.$el && ref.$el.querySelector) {
        return ref.$el.querySelector("textarea") || ref.$el;
      }
      if (ref.querySelector) {
        return ref.querySelector("textarea") || ref;
      }
      return ref;
    },

    inserirPlaceholder(chave) {
      const prop = this.campoFocado === "infoCompl"
        ? "template_informacoes_complementares"
        : "template_descricao_servico";
      const textoAtual = this.templates[prop] || "";
      const textarea = this.getTextareaElement(this.campoFocado);

      if (textarea && typeof textarea.selectionStart === "number" && typeof textarea.selectionEnd === "number") {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const novoTexto = textoAtual.slice(0, start) + chave + textoAtual.slice(end);
        this.templates[prop] = novoTexto;

        const novaPosicao = start + chave.length;
        this.$nextTick(() => {
          if (textarea.focus) textarea.focus();
          if (typeof textarea.setSelectionRange === "function") {
            textarea.setSelectionRange(novaPosicao, novaPosicao);
          }
        });
      } else {
        this.templates[prop] = textoAtual + chave;
      }
    },

    async salvarTemplates() {
      if (!this.temPermissao) {
        this.$vs.notify({
          title: "Acesso Negado",
          text: "Você não tem permissão para salvar configurações fiscais da empresa.",
          color: "danger"
        });
        return;
      }

      if (!this.empresaSelecionadaId) return;

      const invalidosDesc = extrairPlaceholdersInvalidos(this.templates.template_descricao_servico);
      const invalidosInfo = extrairPlaceholdersInvalidos(this.templates.template_informacoes_complementares);
      const todosInvalidos = Array.from(new Set([...invalidosDesc, ...invalidosInfo]));

      if (todosInvalidos.length > 0) {
        this.$vs.notify({
          title: "Placeholder Não Reconhecido",
          text: `Os seguintes placeholders não são reconhecidos pelo sistema: ${todosInvalidos.join(", ")}. Verifique a lista de placeholders disponíveis antes de salvar.`,
          color: "danger",
          time: 8000
        });
        return;
      }

      const emp = this.empresas.find(e => Number(e.id) === Number(this.empresaSelecionadaId)) || this.empresa || {};
      const aliquotaReal = this.aliquotaEfetivaVigente;

      const validacaoTemplates = validarConfiguracaoEmissao(
        {
          template_descricao_servico: this.templates.template_descricao_servico,
          template_informacoes_complementares: this.templates.template_informacoes_complementares,
          aliquota_efetiva_vigente: aliquotaReal,
          competencia: this.competenciaReferencia || undefined
        },
        emp
      );

      if (!validacaoTemplates.valido) {
        this.$vs.notify({
          title: "Template Incompleto",
          text: validacaoTemplates.motivo,
          color: "danger",
          time: 8000
        });
        return;
      }

      this.salvandoTemplates = true;
      this.$vs.loading();
      try {
        await this.$http.put(`empresa/${this.empresaSelecionadaId}`, {
          template_descricao_servico: this.templates.template_descricao_servico,
          template_informacoes_complementares: this.templates.template_informacoes_complementares
        });

        const emp = this.empresas.find(e => Number(e.id) === Number(this.empresaSelecionadaId));
        if (emp) {
          emp.template_descricao_servico = this.templates.template_descricao_servico;
          emp.template_informacoes_complementares = this.templates.template_informacoes_complementares;
        }

        this.$vs.notify({
          title: "Sucesso",
          text: "Templates de NFS-e salvos com sucesso.",
          color: "success"
        });
      } catch (err) {
        await this.tratarErroHttpPadrao(err);
      } finally {
        this.salvandoTemplates = false;
        this.$vs.loading.close();
      }
    },

    ehCompetenciaAtual(competencia) {
      if (!competencia || !this.competenciaReferencia) return false;
      return String(competencia).startsWith(this.competenciaReferencia);
    },

    formatarCompetenciaDisplay(competencia) {
      if (!competencia) return "-";
      return formatarCompetenciaExtenso(competencia);
    },

    onMonthYearSelectedAliquota({ month, year }) {
      const mesStr = String(month + 1).padStart(2, "0");
      this.aliquotaForm.competencia = `${year}-${mesStr}`;
    },

    abrirModalNovaAliquota() {
      if (!this.temPermissao) return;
      this.isEditingAliquota = false;
      this.aliquotaForm = {
        id: null,
        competencia: this.competenciaReferencia || "",
        percentual: ""
      };
      this.modalAliquota = true;
    },

    abrirModalEditarAliquota(item) {
      if (!this.temPermissao) return;
      this.isEditingAliquota = true;
      let comp = item.competencia ? String(item.competencia).slice(0, 7) : (this.competenciaReferencia || "");
      this.aliquotaForm = {
        id: item.id,
        competencia: comp,
        percentual: item.percentual
      };
      this.modalAliquota = true;
    },

    fecharModalAliquota() {
      this.modalAliquota = false;
      this.isModalMonthYearActive = false;
      this.aliquotaForm = { id: null, competencia: "", percentual: "" };
    },

    async salvarAliquota() {
      if (!this.temPermissao) {
        this.$vs.notify({
          title: "Acesso Negado",
          text: "Você não tem permissão para cadastrar ou editar alíquotas da empresa.",
          color: "danger"
        });
        return;
      }

      const compRaw = String(this.aliquotaForm.competencia || "").trim();
      const percRaw = this.aliquotaForm.percentual;

      if (!compRaw) {
        this.$vs.notify({
          title: "Erro de validação",
          text: "Competência é obrigatória (formato YYYY-MM).",
          color: "danger"
        });
        return;
      }

      const numPerc = Number(percRaw);
      if (percRaw === "" || percRaw === null || percRaw === undefined || isNaN(numPerc) || numPerc < 0 || numPerc > 100) {
        this.$vs.notify({
          title: "Erro de validação",
          text: "O percentual da alíquota deve ser um número entre 0 e 100.",
          color: "danger"
        });
        return;
      }

      const match = compRaw.match(/^(\d{4})-(0[1-9]|1[0-2])(?:-(0[1-9]|[12]\d|3[01]))?$/);
      if (!match) {
        this.$vs.notify({
          title: "Formato inválido",
          text: "Competência deve conter ano e mês de dois dígitos (ex: 2026-09).",
          color: "danger"
        });
        return;
      }

      const compPrefix = `${match[1]}-${match[2]}`;
      const duplicada = (this.aliquotas || []).some(a => {
        if (this.isEditingAliquota && this.aliquotaForm.id && Number(a.id) === Number(this.aliquotaForm.id)) {
          return false;
        }
        return a.competencia && String(a.competencia).startsWith(compPrefix);
      });

      if (duplicada) {
        this.$vs.notify({
          title: "Competência duplicada",
          text: `Já existe uma alíquota cadastrada para a competência ${match[2]}/${match[1]}. Edite o registro existente ou escolha outra competência.`,
          color: "danger"
        });
        return;
      }

      const competenciaNormalizada = `${match[1]}-${match[2]}-01`;
      const percentualNumero = parseFloat(numPerc.toFixed(2));

      this.$vs.loading();
      try {
        if (this.isEditingAliquota && this.aliquotaForm.id) {
          await this.$http.put(`empresa_aliquota_efetiva/${this.aliquotaForm.id}`, {
            competencia: competenciaNormalizada,
            percentual: percentualNumero
          });
          this.$vs.notify({
            title: "Sucesso",
            text: "Alíquota efetiva atualizada com sucesso.",
            color: "success"
          });
        } else {
          await this.$http.post("empresa_aliquota_efetiva", {
            empresa_id: this.empresaSelecionadaId,
            competencia: competenciaNormalizada,
            percentual: percentualNumero
          });
          this.$vs.notify({
            title: "Sucesso",
            text: "Alíquota efetiva cadastrada com sucesso.",
            color: "success"
          });
        }

        this.modalAliquota = false;
        await this.carregarDadosEmpresa(this.empresaSelecionadaId);
      } catch (err) {
        if (err && err.response && err.response.status === 409) {
          const mensagemBackend = (err.response.data && (err.response.data.message || err.response.data.error)) ||
            "Já existe uma alíquota cadastrada para esta empresa nesta competência.";
          this.$vs.notify({
            title: "Competência duplicada",
            text: mensagemBackend,
            color: "danger"
          });
        } else {
          await this.tratarErroHttpPadrao(err);
        }
      } finally {
        this.$vs.loading.close();
      }
    },

    confirmarExclusaoAliquota(item) {
      if (!this.temPermissao) {
        this.$vs.notify({
          title: "Acesso Negado",
          text: "Você não tem permissão para excluir alíquotas da empresa.",
          color: "danger"
        });
        return;
      }

      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Excluir Alíquota Efetiva",
        text: `Tem certeza que deseja excluir a alíquota de ${item.percentual}% da competência ${this.formatarCompetenciaDisplay(item.competencia)}?`,
        acceptText: "Excluir",
        cancelText: "Cancelar",
        accept: async () => {
          this.$vs.loading();
          try {
            await this.$http.delete(`empresa_aliquota_efetiva/${item.id}`);
            this.$vs.notify({
              title: "Sucesso",
              text: "Alíquota efetiva excluída com sucesso.",
              color: "success"
            });
            await this.carregarDadosEmpresa(this.empresaSelecionadaId);
          } catch (err) {
            await this.tratarErroHttpPadrao(err);
          } finally {
            this.$vs.loading.close();
          }
        }
      });
    }
  },
  mounted() {
    if (!this.temPermissao) {
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          title: "Acesso Negado",
          text: "Você não tem permissão para acessar esta página.",
          color: "danger"
        });
      }
      this.$router.push("/home");
      return;
    }
    this.carregarEmpresas();
  }
};
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
